// A catalogue product dropped in the 3D view (docs/claude/rendering.md, *A
// product dropped in 3D*): it lands at the plan point under the cursor on what
// the view shows, else on the floor — where a tool's click lands (`land3dAt`,
// viewPicks.ts: X-ray and glass seen through); its footprint is
// previewed there while it is dragged; the drop places it as a drop on the plan
// does — a room or a run starts its drawing on the plan, brought forward.
import {
  type ProductTemplate,
  type Vec2,
} from '../../shared/types';
import { type Pt3 } from '../handles';
import {
  invalidate,
  renderProperties,
  state,
} from './core';
import { armPick } from './armedPick';
import {
  land3dAt,
  toPlan,
  viewer3d,
} from './view3d';
import {
  $,
  placeInstance,
  placementStartsTool,
} from '../main';

/** The catalogue card being dragged: a drag's data can be read only on its
 *  drop, so the card's dragstart records it for the preview. */
export const catalogueDrag: { product: ProductTemplate | null } = { product: null };

/** Where a product dragged over the 3D view would land (the point the cursor is
 *  on: its plan x, y are the drop's), and which product. */
let preview: { at: Pt3; product: ProductTemplate | null } | null = null;

const PRODUCT = 'text/stenal-product';

/** The 3D view takes a catalogue card dropped on it. */
export function installView3dDrop(overlay: HTMLElement): void {
  const accepts = (e: DragEvent): boolean => !!e.dataTransfer?.types.includes(PRODUCT) && state.viewTab === '3d' && !!viewer3d;
  overlay.addEventListener('dragover', (e) => {
    if (!accepts(e)) return;
    e.preventDefault();
    e.dataTransfer!.dropEffect = 'copy';
    $('drop-overlay').classList.add('show');
    const at = land3dAt(e.clientX, e.clientY)?.point;
    preview = at ? { at, product: catalogueDrag.product } : null;
    viewer3d!.redraw();
  });
  overlay.addEventListener('dragleave', (e) => {
    if (e.relatedTarget instanceof Node && overlay.contains(e.relatedTarget)) return;
    clearDropPreview();
  });
  overlay.addEventListener('drop', (e) => {
    if (!accepts(e)) return;
    e.preventDefault();
    clearDropPreview();
    const id = e.dataTransfer?.getData(PRODUCT);
    const t = id ? state.templates.find((x) => x.id === id) : undefined;
    const at = land3dAt(e.clientX, e.clientY)?.point;
    if (t && at) dropProduct3d(t, { x: Math.round(at.x), y: Math.round(at.y) }); // whole mm: no grid shows it here
  });
}

/** The drag left the view or ended: no preview. */
export function clearDropPreview(): void {
  $('drop-overlay').classList.remove('show');
  if (!preview) return;
  preview = null;
  viewer3d?.redraw();
}

/** A catalogue card clicked while the 3D view shows: the next click in it places
 *  the product where a drop there would (an armed pick, so Esc and another tool
 *  let it go); the footprint follows the pointer meanwhile. */
export function armPlacement3d(t: ProductTemplate, prompt: () => string): void {
  const cv = $('view3d-canvas');
  const unarm = (): void => { clearDropPreview(); if (cv.style.cursor === 'copy') cv.style.cursor = ''; };
  armPick({
    kind: 'place-product',
    prompt,
    cursor3d: 'copy',
    move3d: (x, y) => {
      const at = land3dAt(x, y)?.point;
      preview = at ? { at, product: t } : null;
      viewer3d?.redraw();
    },
    click3d: (x, y) => {
      const at = land3dAt(x, y)?.point;
      if (!at) return 'keep';
      unarm();
      dropProduct3d(t, { x: Math.round(at.x), y: Math.round(at.y) });
    },
    cancel: unarm,
  });
}

/** A product dropped in the 3D view at plan point `p`, placed as a drop on the
 *  plan places it (one undo step, selected); a room or a run starts its drawing
 *  there, on the plan. */
export function dropProduct3d(t: ProductTemplate, p: Vec2): void {
  if (placementStartsTool(t)) toPlan();
  placeInstance(t, p);
  renderProperties();
  invalidate();
}

/** Paint where the product dragged over the view lands: its footprint on the
 *  floor under the point the cursor is on, a dashed line up to that point (a
 *  room's or a run's drawing starts there: a cross alone). Lists the plan point
 *  for the web cases (`data-drop`: `x,y,z`). */
export function paintDropPreview(ctx: CanvasRenderingContext2D, cv: HTMLCanvasElement): void {
  cv.dataset.drop = preview ? `${Math.round(preview.at.x)},${Math.round(preview.at.y)},${Math.round(preview.at.z)}` : '';
  const v = viewer3d;
  if (!preview || !v) return;
  const { at, product: t } = preview;
  const hit = v.project(at), foot = v.project({ x: at.x, y: at.y, z: 0 });
  ctx.save();
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = '#7fb2ff';
  if (t && !placementStartsTool(t)) {
    const hw = t.width / 2, hd = t.depth / 2;
    const ring = [[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]].map(([x, y]) => v.project({ x: at.x + x, y: at.y + y, z: 0 }));
    if (ring.every((q) => q)) {
      ctx.beginPath();
      ring.forEach((q, i) => (i ? ctx.lineTo(q!.x, q!.y) : ctx.moveTo(q!.x, q!.y)));
      ctx.closePath();
      ctx.fillStyle = 'rgba(127, 178, 255, 0.25)';
      ctx.fill();
      ctx.stroke();
    }
  }
  if (hit && foot && Math.hypot(hit.x - foot.x, hit.y - foot.y) > 2) {
    ctx.setLineDash([5, 4]);
    ctx.beginPath(); ctx.moveTo(hit.x, hit.y); ctx.lineTo(foot.x, foot.y); ctx.stroke();
    ctx.setLineDash([]);
  }
  if (hit) {
    ctx.beginPath();
    ctx.moveTo(hit.x - 6, hit.y); ctx.lineTo(hit.x + 6, hit.y);
    ctx.moveTo(hit.x, hit.y - 6); ctx.lineTo(hit.x, hit.y + 6);
    ctx.stroke();
  }
  ctx.restore();
}
